import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import toast from 'react-hot-toast';

/**
 * SubAdminPermissionGuard
 *
 * Wraps a route and redirects SubAdmins who lack the required module access.
 * Super-admins (is_admin=true) always pass through.
 * While permissions are still loading (myPermissions is null), renders children
 * to avoid a flash redirect before data arrives.
 *
 * Props:
 *   moduleKey  — key from MODULE_NAMES in useAdminPermissions (e.g. 'airliners', 'saxUsers')
 *   action     — optional action to check (e.g. 'index', 'add'). Defaults to 'index'.
 *   redirectTo — where to send the user on denial.
 *                Defaults to '/admin/settings' (account settings / profile), matching
 *                the PHP behaviour of redirecting to 'profile' on permission denial.
 *   children   — the page component to render when access is granted.
 *
 * Usage:
 *   <SubAdminPermissionGuard moduleKey="airliners">
 *     <AirlinerManagement />
 *   </SubAdminPermissionGuard>
 */
const SubAdminPermissionGuard = ({
  moduleKey,
  action = 'index',
  redirectTo = '/admin/settings',
  children,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const { myPermissions } = useAppSelector((state) => state.admin);
  const { perms } = useAdminPermissions();

  // Full admins always pass through — only SubAdmins are subject to permission checks
  const isAdmin = !user || user.user_type === 'Admin' || user.is_admin === 1;

  // Still loading permissions — hold rendering to prevent the child component
  // from mounting and firing API calls before we know if access is granted.
  // Full admins skip this wait since they never need permission data.
  const isLoading = !isAdmin && !myPermissions;

  // Determine access
  const modulePerms = perms[moduleKey];
  const hasAccess = isAdmin || !modulePerms || modulePerms[action] === true;

  // Fire toast once when access is denied
  useEffect(() => {
    if (!isLoading && !hasAccess && !isAdmin) {
      toast.error('Access denied. Please contact the account administrator.');
    }
  }, [moduleKey, action, hasAccess, isLoading, isAdmin]);

  // Wait for permissions to load before making an access decision
  if (isLoading) {
    return null;
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default SubAdminPermissionGuard;
